import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { getAuthInfo } from '@/utils/auth'
import { getAccumulationHistory } from '@/api/member'
import {
  formatProcessAt,
  formatRewardAmountDisplay,
  getRewardAmountStyle,
  rewardStatusLabel,
  rewardTypeLabel,
  type RewardAmountStyle
} from '@/utils/rewardDisplay'
import type { MemberProfile } from '@/types/member'
import type { AccumulationHistoryItem } from '@/api/member'
import styles from './AccumulationHistory.module.css'

/** 적립 내역 한 건 (UI용) */
interface HistoryItem {
  id: string
  date: string
  reason: string
  amountDisplay: string
  amountType: RewardAmountStyle
}

/** orderSourceType·orderId 기준 적립 사유 문구 */
function buildReason(item: AccumulationHistoryItem): string {
  const typeLabel = rewardTypeLabel(item.rewardType)
  const statusLabel = rewardStatusLabel(item.status)

  if (item.orderSourceType === 'AGENT') {
    return ['매장적립', typeLabel, statusLabel].filter(Boolean).join(' ')
  }

  const orderId = item.orderId?.trim()
  if (orderId) {
    return [`주문 ${orderId}`, typeLabel, statusLabel].filter(Boolean).join(' ')
  }

  return [typeLabel, statusLabel].filter(Boolean).join(' ')
}

/** API 적립 내역 한 건 → UI HistoryItem */
function mapHistoryItem(apiItem: AccumulationHistoryItem): HistoryItem {
  return {
    id: String(apiItem.id),
    date: formatProcessAt(apiItem.processAt),
    reason: buildReason(apiItem),
    amountDisplay: formatRewardAmountDisplay(
      apiItem.quantity,
      apiItem.rewardType,
      apiItem.status
    ),
    amountType: getRewardAmountStyle(apiItem.status)
  }
}

const ITEMS_PER_PAGE = 20
const RECENT_DAYS = 93

/** 목 데이터 - state 없이 직접 진입 시 */
const MOCK_MEMBER: MemberProfile = {
  searchStatus: 'SHOP_CUSTOMER',
  customerCode: '1234567890123',
  nickname: '홍길*',
  stampCount: 8,
  pointAmount: 2580,
  couponCount: 3
}

/** 페이지네이션 정보 (API 응답 기반) */
interface PaginationState {
  totalCount: number
  totalPages: number
  hasNext: boolean
  hasPrevious: boolean
}

const initialPagination: PaginationState = {
  totalCount: 0,
  totalPages: 0,
  hasNext: false,
  hasPrevious: false
}

export default function AccumulationHistory() {
  const navigate = useNavigate()
  const location = useLocation()

  const [member, setMember] = useState<MemberProfile | null>(null)
  const [history, setHistory] = useState<HistoryItem[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState<PaginationState>(initialPagination)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type })
    const t = setTimeout(() => setToast(null), 3000)
    return () => clearTimeout(t)
  }, [])

  const goBack = () => {
    navigate('/dashboard/member', { state: location.state })
  }

  const goToPage = (page: number | 'prev' | 'next') => {
    if (page === 'prev') {
      setCurrentPage((p) => (p > 1 ? p - 1 : p))
    } else if (page === 'next') {
      setCurrentPage((p) => (p < pagination.totalPages ? p + 1 : p))
    } else {
      setCurrentPage(page)
    }
  }

  useEffect(() => {
    const stateMember = (location.state as { member?: MemberProfile })?.member
    setMember(stateMember ?? MOCK_MEMBER)
  }, [location.state])

  useEffect(() => {
    const m = member
    const authInfo = getAuthInfo()
    const projectCode = authInfo?.projectCode?.trim()
    const shopCode = authInfo?.shopCode?.trim()
    const brandCode = authInfo?.brandCode?.trim()
    const customerCode = m?.customerCode?.trim()

    if (!m || !projectCode || !shopCode || !brandCode || !customerCode) {
      if (m) {
        setError('매장 정보를 불러올 수 없습니다.')
        setHistory([])
        setPagination(initialPagination)
      }
      return
    }

    setError(null)
    setLoading(true)
    setHistory([])
    const controller = new AbortController()
    const { signal } = controller

    const pageIndex = currentPage - 1

    getAccumulationHistory(projectCode, shopCode, customerCode, brandCode, {
      recentDays: RECENT_DAYS,
      page: pageIndex,
      size: ITEMS_PER_PAGE,
      signal
    })
      .then((historyData) => {
        if (signal.aborted) return
        setHistory(historyData.rewards.map(mapHistoryItem))
        setPagination({
          totalCount: historyData.totalCount,
          totalPages: historyData.totalPages,
          hasNext: historyData.hasNext,
          hasPrevious: historyData.hasPrevious
        })
      })
      .catch((err: unknown) => {
        if (signal.aborted) return
        const message =
          err instanceof Error ? err.message : '데이터를 불러오는 중 오류가 발생했습니다.'
        setError(message)
        showToast(message, 'error')
        setHistory([])
        setPagination(initialPagination)
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false)
      })

    return () => {
      controller.abort()
    }
  }, [member, currentPage, showToast])

  if (!member) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <button type="button" className={styles.backButton} onClick={goBack} aria-label="뒤로 가기">
              ←
            </button>
            <h1 className={styles.pageTitle}>적립 내역</h1>
          </div>
        </header>
        <main className={styles.mainContainer}>
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📋</div>
            <div className={styles.emptyMessage}>회원 정보가 없습니다.</div>
          </div>
        </main>
      </div>
    )
  }

  const amountClass =
    (type: HistoryItem['amountType']) =>
      type === 'positive'
        ? styles.historyAmountPositive
        : type === 'negative'
          ? styles.historyAmountNegative
          : styles.historyAmountCancelled

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button type="button" className={styles.backButton} onClick={goBack} aria-label="뒤로 가기">
            ←
          </button>
          <h1 className={styles.pageTitle}>적립 내역</h1>
        </div>
      </header>

      <main className={styles.mainContainer}>
        <section className={styles.memberInfoCard}>
          <div className={styles.memberHeader}>
            <div className={styles.memberDetails}>
              <div className={styles.memberName}>{member.nickname}</div>
              <div className={styles.memberBarcode}>{member.customerCode}</div>
            </div>
          </div>
        </section>

        <section className={styles.historySection}>
          <h2 className={styles.sectionTitle}>적립 내역</h2>

          {loading && history.length === 0 && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📋</div>
              <div className={styles.emptyMessage}>적립 내역을 불러오는 중...</div>
            </div>
          )}
          {error && history.length === 0 && (
            <div className={styles.emptyState}>
              <div className={styles.emptyMessage}>{error}</div>
            </div>
          )}
          {!loading && !error && history.length === 0 && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📋</div>
              <div className={styles.emptyMessage}>적립 내역이 없습니다.</div>
            </div>
          )}
          {!error && history.length > 0 && (
            <>
              <table className={styles.historyTable}>
                <thead>
                  <tr>
                    <th>적립일</th>
                    <th>적립 사유</th>
                    <th>적립량</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item) => (
                    <tr key={`${currentPage}-${item.id}`}>
                      <td className={styles.historyDate}>{item.date}</td>
                      <td className={styles.historyReason}>{item.reason}</td>
                      <td className={`${styles.historyAmount} ${amountClass(item.amountType)}`}>
                        {item.amountDisplay}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageButton}
                  onClick={() => goToPage('prev')}
                  disabled={!pagination.hasPrevious}
                  aria-label="이전 페이지"
                >
                  ←
                </button>
                {Array.from({ length: Math.max(1, pagination.totalPages) }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`${styles.pageButton} ${currentPage === p ? styles.active : ''}`}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  className={styles.pageButton}
                  onClick={() => goToPage('next')}
                  disabled={!pagination.hasNext}
                  aria-label="다음 페이지"
                >
                  →
                </button>
                <div className={styles.pageInfo}>
                  총 {pagination.totalCount}건 (페이지 {currentPage}/{pagination.totalPages || 1})
                </div>
              </div>
            </>
          )}
        </section>
      </main>

      {toast && (
        <div
          className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}
          role="alert"
        >
          {toast.message}
        </div>
      )}
    </div>
  )
}
